import { ArrowRight, Check, Volume2, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { player } from '../audio/player';
import { GreekText } from '../components/GreekText';
import { StepHead } from '../components/StepHead';
import { frameOf, staffOf } from '../content';
import type { Item } from '../content/types';
import { clipFor, itemVoice, speechRange } from '../lib/voice';

interface Props {
  items: Item[];
  onDone: () => void;
  /** A listening-only day: speaking waits. */
  quiet?: boolean;
}

/** Rounds in a run: enough to hear each phrase about twice, kept short. */
function roundsFor(n: number) {
  return Math.min(6, Math.max(4, n * 2));
}

/** A play order where no phrase comes twice in a row, and every phrase comes at least once. */
function playOrder(items: Item[], rounds: number, random = Math.random): Item[] {
  const order: Item[] = [];
  let pool: Item[] = [];
  while (order.length < rounds) {
    if (pool.length === 0) pool = [...items].sort(() => random() - 0.5);
    const next = pool.findIndex((i) => i.id !== order[order.length - 1]?.id);
    order.push(pool.splice(next >= 0 ? next : 0, 1)[0]);
  }
  return order;
}

/**
 * "Which did you hear?" Phrases on one pattern differ in a small word (έναν καφέ / ένα νερό /
 * μια μπίρα). Hearing the difference, and having to process it for meaning, comes before
 * producing it. Quick rounds, each phrase in its speaker's voice, nothing graded.
 */
export function ContrastStep({ items, onDone, quiet = false }: Props) {
  const rounds = roundsFor(items.length);
  // Drawn once per set of phrases: a re-render (the window regaining focus) must not reshuffle a round.
  const key = items.map((i) => i.id).join(' ');
  const order = useMemo(() => playOrder(items, rounds), [key, rounds]);
  const [round, setRound] = useState(0);
  const [pick, setPick] = useState<string | null>(null);
  const [score, setScore] = useState(0);
  const [started, setStarted] = useState(false);
  const nextRef = useRef<HTMLButtonElement>(null);
  const frame = frameOf(items[0]);
  const target = order[Math.min(round, order.length - 1)];
  const finished = round >= rounds;

  const play = (item: Item) => {
    const clip = clipFor(item, itemVoice(item, staffOf(item)));
    player.play(clip.src, { ...speechRange(clip), tag: `contrast:${item.id}` });
  };

  useEffect(() => () => player.stop(), []);

  const start = () => {
    setStarted(true);
    play(order[0]);
  };

  const choose = (id: string) => {
    if (pick || !started) return;
    setPick(id);
    if (id === target.id) setScore((s) => s + 1);
    requestAnimationFrame(() => nextRef.current?.focus());
  };

  const next = () => {
    const r = round + 1;
    setPick(null);
    setRound(r);
    if (r < rounds) play(order[r]);
  };

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey || !started || pick || finished) return;
      const n = Number(e.key);
      if (n >= 1 && n <= items.length) {
        e.preventDefault();
        choose(items[n - 1].id);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });

  return (
    <div className="step">
      <div className="stack">
        <StepHead kind="contrast">
          {started && !finished && (
            <span className="chip">
              {Math.min(round + 1, rounds)} of {rounds}
            </span>
          )}
        </StepHead>
        <h2 className="english-cue" id="contrast-q">
          {finished ? 'Your ear is tuning in' : 'Which did you hear?'}
        </h2>
        {frame && !finished && (
          <p className="muted">
            Same pattern,{' '}
            <span className="greek" lang="el">
              {frame.pattern}
            </span>
            : listen for the part that changes (underlined).
          </p>
        )}
      </div>

      {!finished && (
        <>
          {started && (
            <button type="button" className="btn btn-secondary" style={{ alignSelf: 'flex-start' }} onClick={() => play(target)}>
              <Volume2 size={20} aria-hidden="true" /> Play again
            </button>
          )}
          <div className="options" role="group" aria-labelledby="contrast-q" data-answered={pick ? true : undefined}>
            {items.map((item, i) => {
              const result = pick ? (item.id === target.id ? 'right' : item.id === pick ? 'wrong' : undefined) : undefined;
              return (
                <button
                  key={item.id}
                  type="button"
                  className="option"
                  data-result={result}
                  disabled={!started || Boolean(pick)}
                  onClick={() => choose(item.id)}
                  aria-keyshortcuts={String(i + 1)}
                >
                  {result && <span className="option-key" aria-hidden="true">{result === 'right' ? <Check size={18} /> : <X size={18} />}</span>}
                  <span className="stack" style={{ gap: 2 }}>
                    <GreekText text={item.text} slot={item.slot?.slot} size="body" />
                    {/* The meaning would let the English decide; it comes with the answer. */}
                    {pick && <span className="small muted">{item.meaning}</span>}
                  </span>
                  {result && <span className="sr-only">{result === 'right' ? '(what you heard)' : '(your pick)'}</span>}
                </button>
              );
            })}
          </div>
          {pick && (
            <p className={`result ${pick === target.id ? 'result-right' : 'result-wrong'}`} role="status">
              {pick === target.id ? <Check size={22} aria-hidden="true" /> : <X size={22} aria-hidden="true" />}
              {pick === target.id ? 'Right' : 'Not quite. Play it again and listen for the underlined part.'}
            </p>
          )}
        </>
      )}

      {finished && (
        <div className="panel-quiet stack reveal">
          <p className="section-title">
            {score} of {rounds} by ear
          </p>
          <p className="muted">{quiet ? 'Next time you can speak, try saying them.' : 'Now try saying them.'} The underlined part is what changes.</p>
        </div>
      )}

      <div className="actions">
        {!started && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={start}>
            <Volume2 size={20} aria-hidden="true" /> Start listening
          </button>
        )}
        {started && pick && !finished && (
          <button ref={nextRef} type="button" className="btn btn-primary btn-block btn-lg" onClick={next}>
            {round + 1 < rounds ? 'Next' : 'Finish'} <ArrowRight size={20} aria-hidden="true" />
          </button>
        )}
        {finished && (
          <button type="button" className="btn btn-primary btn-block btn-lg" onClick={onDone}>
            Continue <ArrowRight size={20} aria-hidden="true" />
          </button>
        )}
        {!finished && (
          <button type="button" className="btn btn-ghost btn-block" onClick={onDone}>
            Skip this
          </button>
        )}
      </div>
    </div>
  );
}
